// Individual Validator Implementations
import type { Validator, ValidationContext, ValidationResult } from './types';

/**
 * Required Field Validator
 */
export class RequiredValidator implements Validator {
  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;
    
    // Check for empty values
    if (value === undefined || value === null || value === '') {
      return {
        isValid: false,
        message: `${fieldName} is required`
      };
    }
    
    // Check for empty arrays (checkboxes, multi-select)
    if (Array.isArray(value) && value.length === 0) {
      return {
        isValid: false,
        message: `${fieldName} is required`
      };
    }
    
    // Check for empty strings after trim
    if (typeof value === 'string' && value.trim() === '') {
      return {
        isValid: false,
        message: `${fieldName} is required`
      };
    }
    
    return { isValid: true };
  }
}

/**
 * Email Validator
 */
export class EmailValidator implements Validator {
  private readonly emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  validate(context: ValidationContext): ValidationResult {
    const { value } = context;

    // Skip if empty (use RequiredValidator for required check)
    if (!value) {
      return { isValid: true };
    }
    
    if (!this.emailRegex.test(value)) {
      return {
        isValid: false,
        message: 'Please enter a valid email address'
      };
    }
    
    return { isValid: true };
  }
}

/**
 * URL Validator
 */
export class UrlValidator implements Validator {
  validate(context: ValidationContext): ValidationResult {
    const { value } = context;

    // Skip if empty
    if (!value) {
      return { isValid: true };
    }
    
    try {
      new URL(value);
      return { isValid: true };
    } catch {
      return {
        isValid: false,
        message: 'Please enter a valid URL'
      };
    }
  }
}

/**
 * Min Length Validator
 */
export class MinLengthValidator implements Validator {
  constructor(private minLength: number) {}
  
  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;
    
    // Skip if empty
    if (!value) {
      return { isValid: true };
    }
    
    if (typeof value === 'string' && value.length < this.minLength) {
      return {
        isValid: false,
        message: `${fieldName} must be at least ${this.minLength} characters`
      };
    }
    
    return { isValid: true };
  }
}

/**
 * Max Length Validator
 */
export class MaxLengthValidator implements Validator {
  constructor(private maxLength: number) {}
  
  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;
    
    // Skip if empty
    if (!value) {
      return { isValid: true };
    }
    
    if (typeof value === 'string' && value.length > this.maxLength) {
      return {
        isValid: false,
        message: `${fieldName} must not exceed ${this.maxLength} characters`
      };
    }
    
    return { isValid: true };
  }
}

/**
 * Pattern Validator (Regex)
 */
export class PatternValidator implements Validator {
  private regex: RegExp;
  
  constructor(pattern: string | RegExp, private customMessage?: string) {
    this.regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
  }
  
  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;
    
    // Skip if empty
    if (!value) {
      return { isValid: true };
    }
    
    if (typeof value === 'string' && !this.regex.test(value)) {
      return {
        isValid: false,
        message: this.customMessage || `${fieldName} format is invalid`
      };
    }
    
    return { isValid: true };
  }
}

/**
 * Number Range Validator
 */
export class NumberRangeValidator implements Validator {
  constructor(
    private min?: number,
    private max?: number
  ) {}
  
  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;
    
    // Skip if empty
    if (value === undefined || value === null || value === '') {
      return { isValid: true };
    }
    
    const numValue = Number(value);
    
    if (isNaN(numValue)) {
      return {
        isValid: false,
        message: `${fieldName} must be a valid number`
      };
    }
    
    if (this.min !== undefined && numValue < this.min) {
      return {
        isValid: false,
        message: `${fieldName} must be at least ${this.min}`
      };
    }
    
    if (this.max !== undefined && numValue > this.max) {
      return {
        isValid: false,
        message: `${fieldName} must not exceed ${this.max}`
      };
    }
    
    return { isValid: true };
  }
}

/**
 * Array Length Validator (for multi-select, checkboxes)
 */
export class ArrayLengthValidator implements Validator {
  constructor(
    private minItems?: number,
    private maxItems?: number
  ) {}
  
  validate(context: ValidationContext): ValidationResult {
    const { value } = context;

    // Skip if empty
    if (!value) {
      return { isValid: true };
    }
    
    if (!Array.isArray(value)) {
      return { isValid: true };
    }
    
    if (this.minItems !== undefined && value.length < this.minItems) {
      return {
        isValid: false,
        message: `Please select at least ${this.minItems} option(s)`
      };
    }
    
    if (this.maxItems !== undefined && value.length > this.maxItems) {
      return {
        isValid: false,
        message: `Please select no more than ${this.maxItems} option(s)`
      };
    }
    
    return { isValid: true };
  }
}

/**
 * File Type Validator
 */
export class FileTypeValidator implements Validator {
  private allowedTypes: string[];
  
  constructor(accept: string) {
    this.allowedTypes = accept.split(',').map(t => t.trim().toLowerCase());
  }
  
  validate(context: ValidationContext): ValidationResult {
    const { value } = context;

    // Skip if empty
    if (!value) {
      return { isValid: true };
    }
    
    const files = Array.isArray(value) ? value : [value];
    
    for (const file of files) {
      if (file && file.type) {
        const fileType = file.type.toLowerCase();
        const fileExt = `.${file.name.split('.').pop()?.toLowerCase()}`;
        
        const isValid = this.allowedTypes.some(allowed => {
          if (allowed.startsWith('.')) {
            return fileExt === allowed;
          }
          if (allowed.endsWith('/*')) {
            return fileType.startsWith(allowed.replace('/*', ''));
          }
          return fileType === allowed;
        });
        
        if (!isValid) {
          return {
            isValid: false,
            message: `${file.name} is not an allowed file type`
          };
        }
      }
    }
    
    return { isValid: true };
  }
}

/**
 * File Size Validator
 */
export class FileSizeValidator implements Validator {
  constructor(
    private maxSizeBytes: number,
    private maxSizeMB?: number
  ) {}
  
  validate(context: ValidationContext): ValidationResult {
    const { value } = context;

    // Skip if empty
    if (!value) {
      return { isValid: true };
    }
    
    const files = Array.isArray(value) ? value : [value];
    
    for (const file of files) {
      if (file && file.size > this.maxSizeBytes) {
        const displaySize = this.maxSizeMB || (this.maxSizeBytes / 1024 / 1024).toFixed(2);
        return {
          isValid: false,
          message: `${file.name} exceeds maximum file size of ${displaySize}MB`
        };
      }
    }
    
    return { isValid: true };
  }
}

/**
 * Phone Number Validator
 */
export class PhoneValidator implements Validator {
  // Basic international phone regex
  private readonly phoneRegex = /^[\d\s\-+()]+$/;

  validate(context: ValidationContext): ValidationResult {
    const { value } = context;

    // Skip if empty
    if (!value) {
      return { isValid: true };
    }
    
    // Remove spaces for length check
    const digitsOnly = value.replace(/[\s\-+()]/g, '');

    if (!this.phoneRegex.test(value) || digitsOnly.length < 10) {
      return {
        isValid: false,
        message: 'Please enter a valid phone number'
      };
    }
    
    return { isValid: true };
  }
}

/**
 * Date Range Validator
 */
export class DateRangeValidator implements Validator {
  constructor(
    private minDate?: Date | string,
    private maxDate?: Date | string
  ) {}
  
  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;
    
    // Skip if empty
    if (!value) {
      return { isValid: true };
    }
    
    const date = new Date(value);
    
    if (isNaN(date.getTime())) {
      return {
        isValid: false,
        message: `${fieldName} must be a valid date`
      };
    }
    
    if (this.minDate) {
      const min = new Date(this.minDate);
      if (date < min) {
        return {
          isValid: false,
          message: `${fieldName} must be on or after ${min.toLocaleDateString()}`
        };
      }
    }
    
    if (this.maxDate) {
      const max = new Date(this.maxDate);
      if (date > max) {
        return {
          isValid: false,
          message: `${fieldName} must be on or before ${max.toLocaleDateString()}`
        };
      }
    }
    
    return { isValid: true };
  }
}

/**
 * Custom Function Validator
 */
export class CustomValidator implements Validator {
  constructor(
    private validationFn: (context: ValidationContext) => boolean | Promise<boolean>,
    private message: string
  ) {}
  
  async validate(context: ValidationContext): Promise<ValidationResult> {
    const isValid = await this.validationFn(context);
    
    if (!isValid) {
      return {
        isValid: false,
        message: this.message
      };
    }
    
    return { isValid: true };
  }
}

/**
 * Matrix Field Validator
 * Validates matrix and rating-matrix fields with requireAllRows option
 */
export class MatrixValidator implements Validator {
  constructor(
    private rows?: Array<{ label: string; value: string }>,
    private requireAllRows?: boolean
  ) {}

  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;

    // Skip if requireAllRows is not enabled
    if (!this.requireAllRows) {
      return { isValid: true };
    }

    // Skip if no rows configured
    if (!this.rows || this.rows.length === 0) {
      return { isValid: true };
    }

    // Check if value is an object (matrix response format)
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return {
        isValid: false,
        message: `Please answer all rows in ${fieldName}`
      };
    }

    // Get answered rows
    const answeredRows = Object.keys(value).filter(key => {
      const answer = value[key];
      return answer !== null && answer !== undefined && answer !== '';
    });

    // Check if all rows are answered
    const allRowsAnswered = this.rows.every(row => {
      const rowValue = value[row.value];
      return rowValue !== null && rowValue !== undefined && rowValue !== '';
    });

    if (!allRowsAnswered) {
      const unansweredCount = this.rows.length - answeredRows.length;
      return {
        isValid: false,
        message: `Please answer all ${this.rows.length} rows in ${fieldName} (${unansweredCount} remaining)`
      };
    }

    return { isValid: true };
  }
}

/**
 * Color Validator - validates hex color format
 */
export class ColorValidator implements Validator {
  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;

    // Skip if empty
    if (!value) {
      return { isValid: true };
    }

    // Check if valid hex color (#000000 or #000)
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

    if (!hexColorRegex.test(value)) {
      return {
        isValid: false,
        message: `${fieldName} must be a valid hex color (e.g., #FF5733)`
      };
    }

    return { isValid: true };
  }
}

/**
 * Currency Validator - validates currency format
 */
export class CurrencyValidator implements Validator {
  constructor(
    private min?: number,
    private max?: number
  ) {}

  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;

    // Skip if empty
    if (!value && value !== 0) {
      return { isValid: true };
    }

    // Convert to number
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(numValue)) {
      return {
        isValid: false,
        message: `${fieldName} must be a valid currency amount`
      };
    }

    // Check decimal parts
    const decimalParts = String(value).split('.');
    if (decimalParts[1] && decimalParts[1].length > 2) {
      return {
        isValid: false,
        message: `${fieldName} can have at most 2 decimal places`
      };
    }

    // Check min/max
    if (this.min !== undefined && numValue < this.min) {
      return {
        isValid: false,
        message: `${fieldName} must be at least ${this.min}`
      };
    }

    if (this.max !== undefined && numValue > this.max) {
      return {
        isValid: false,
        message: `${fieldName} must not exceed ${this.max}`
      };
    }

    return { isValid: true };
  }
}

/**
 * Address Validator - validates address object has required fields
 */
export class AddressValidator implements Validator {
  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;

    // Skip if empty
    if (!value) {
      return { isValid: true };
    }

    // Check if value is an object
    if (typeof value !== 'object' || Array.isArray(value)) {
      return {
        isValid: false,
        message: `${fieldName} must be a valid address`
      };
    }

    // Check required address fields
    const requiredFields = ['street', 'city', 'postal', 'country'];
    const missingFields = requiredFields.filter(field => !value[field] || value[field].trim() === '');

    if (missingFields.length > 0) {
      return {
        isValid: false,
        message: `Please complete all required address fields`
      };
    }

    return { isValid: true };
  }
}

/**
 * Country Validator - validates country code
 */
export class CountryValidator implements Validator {
  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;

    // Skip if empty
    if (!value) {
      return { isValid: true };
    }

    // Check if valid 2-letter country code
    const countryCodeRegex = /^[A-Z]{2}$/;

    if (!countryCodeRegex.test(value)) {
      return {
        isValid: false,
        message: `${fieldName} must be a valid country code`
      };
    }

    return { isValid: true };
  }
}

/**
 * Signature Validator - validates signature data exists
 */
export class SignatureValidator implements Validator {
  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;

    // Skip if not required and empty
    if (!value) {
      return { isValid: true };
    }

    // Check if value is a data URL (base64 image)
    if (typeof value !== 'string' || !value.startsWith('data:image/')) {
      return {
        isValid: false,
        message: `${fieldName} must be a valid signature`
      };
    }

    return { isValid: true };
  }
}

/**
 * Ranking Validator - validates all items are ranked
 */
export class RankingValidator implements Validator {
  constructor(private expectedItemCount?: number) {}

  validate(context: ValidationContext): ValidationResult {
    const { value, fieldName } = context;

    // Skip if empty
    if (!value) {
      return { isValid: true };
    }

    // Check if value is an array
    if (!Array.isArray(value)) {
      return {
        isValid: false,
        message: `${fieldName} must contain ranked items`
      };
    }

    // If expected count is specified, verify all items are ranked
    if (this.expectedItemCount !== undefined && value.length !== this.expectedItemCount) {
      return {
        isValid: false,
        message: `Please rank all ${this.expectedItemCount} items`
      };
    }

    return { isValid: true };
  }
}

/**
 * Image Choice Validator - validates image selection
 */
export class ImageChoiceValidator implements Validator {
  constructor(
    private minSelections?: number,
    private maxSelections?: number
  ) {}

  validate(context: ValidationContext): ValidationResult {
    const { value } = context;

    // Skip if empty
    if (!value) {
      return { isValid: true };
    }

    // Handle both single and multiple selections
    const selections = Array.isArray(value) ? value : [value];

    if (this.minSelections !== undefined && selections.length < this.minSelections) {
      return {
        isValid: false,
        message: `Please select at least ${this.minSelections} option(s)`
      };
    }

    if (this.maxSelections !== undefined && selections.length > this.maxSelections) {
      return {
        isValid: false,
        message: `Please select no more than ${this.maxSelections} option(s)`
      };
    }

    return { isValid: true };
  }
}
